// -- [Signup form draft: localStorage persistence and URL prefill]

const DRAFT_KEY = 'drivible_demo_form_draft'

export interface SignupDraft {
  dealership: string
  website: string
  firstName: string
  lastName: string
  email: string
  mobile: string
}

const EMPTY: SignupDraft = {
  dealership: '',
  website: '',
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
}

function safeJsonParse<T>(raw: string | null, fallback: T): T {
  if (raw == null || raw === '') return fallback
  try {
    const parsed = JSON.parse(raw) as T
    return typeof parsed === 'object' && parsed != null ? parsed : fallback
  } catch {
    return fallback
  }
}

/** -- Read draft from localStorage; returns only string fields with sensible defaults */
export function getStoredDraft(): SignupDraft {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    const obj = safeJsonParse<Record<string, unknown>>(raw, {})
    return {
      dealership: typeof obj.dealership === 'string' ? obj.dealership : '',
      website: typeof obj.website === 'string' ? obj.website : '',
      firstName: typeof obj.firstName === 'string' ? obj.firstName : '',
      lastName: typeof obj.lastName === 'string' ? obj.lastName : '',
      email: typeof obj.email === 'string' ? obj.email : '',
      mobile: typeof obj.mobile === 'string' ? obj.mobile : '',
    }
  } catch {
    return { ...EMPTY }
  }
}

/** -- Write draft to localStorage (only called when form values change) */
export function setStoredDraft(draft: SignupDraft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // -- [ignore quota / private mode]
  }
}

/** -- Clear draft (called after successful submit + modal close) */
export function clearStoredDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY)
  } catch {
    // -- [ignore]
  }
}

/** -- Parse query string for prefill params; URL-decoded. Supports name (split into first/last) */
export function getUrlPrefill(): Partial<SignupDraft> {
  const params = new URLSearchParams(window.location.search)
  const get = (k: string) => params.get(k)?.trim() ?? ''
  const name = get('name')
  const firstName = get('firstName') || (name ? name.split(/\s+/)[0] ?? '' : '')
  const lastName = get('lastName') || (name ? name.split(/\s+/).slice(1).join(' ') : '')
  const out: Partial<SignupDraft> = {}
  if (get('dealership')) out.dealership = get('dealership')
  if (get('website')) out.website = get('website')
  if (firstName) out.firstName = firstName
  if (lastName) out.lastName = lastName
  if (get('email')) out.email = get('email')
  if (get('mobile')) out.mobile = get('mobile')
  return out
}

/** -- Merge stored draft with URL prefill; URL values override stored for that field */
export function getInitialFormState(): SignupDraft {
  const stored = getStoredDraft()
  const url = getUrlPrefill()
  return {
    dealership: url.dealership ?? stored.dealership,
    website: url.website ?? stored.website,
    firstName: url.firstName ?? stored.firstName,
    lastName: url.lastName ?? stored.lastName,
    email: url.email ?? stored.email,
    mobile: url.mobile ?? stored.mobile,
  }
}

/** -- True if query has showFormOnPageLoad=true (open signup modal on load) */
export function shouldOpenSignupOnLoad(): boolean {
  const params = new URLSearchParams(window.location.search)
  return params.get('showFormOnPageLoad') === 'true'
}

// -- [Submitted demo: remember email so we can show "already submitted" tick after refresh]
const SUBMITTED_EMAIL_KEY = 'drivible_demo_submitted_email'

/** -- Store email after successful demo submission (for green Setup buttons + friendly reminder tick) */
export function setSubmittedDemoEmail(email: string): void {
  try {
    if (email && typeof email === 'string') {
      localStorage.setItem(SUBMITTED_EMAIL_KEY, email.trim())
    }
  } catch {
    // -- [ignore]
  }
}

/** -- Read stored submitted email; null if none */
export function getSubmittedDemoEmail(): string | null {
  try {
    const v = localStorage.getItem(SUBMITTED_EMAIL_KEY)
    return v && v.trim() ? v.trim() : null
  } catch {
    return null
  }
}

// -- [Last submission result: show success view again when reopening modal]
const LAST_SUBMISSION_KEY = 'drivible_demo_last_submission'

export interface LastSubmissionResult {
  dealership: string
  firstName: string
  lastName: string
  email: string
  mobile: string
  interestLevelLabel: string
  interestOptions: readonly boolean[]
}

/** -- Store last submission so modal can show success view when reopened */
export function setLastSubmission(result: LastSubmissionResult): void {
  try {
    localStorage.setItem(LAST_SUBMISSION_KEY, JSON.stringify(result))
  } catch {
    // -- [ignore]
  }
}

/** -- Read last submission for success view; null if none */
export function getLastSubmission(): LastSubmissionResult | null {
  try {
    const raw = localStorage.getItem(LAST_SUBMISSION_KEY)
    if (!raw?.trim()) return null
    const obj = safeJsonParse<Record<string, unknown>>(raw, {})
    const interestOptions = obj.interestOptions as unknown
    const opts = Array.isArray(interestOptions) && interestOptions.length === 5
      ? interestOptions.every((v) => typeof v === 'boolean')
        ? (interestOptions as boolean[])
        : null
      : null
    if (opts == null) return null
    return {
      dealership: typeof obj.dealership === 'string' ? obj.dealership : '',
      firstName: typeof obj.firstName === 'string' ? obj.firstName : '',
      lastName: typeof obj.lastName === 'string' ? obj.lastName : '',
      email: typeof obj.email === 'string' ? obj.email : '',
      mobile: typeof obj.mobile === 'string' ? obj.mobile : '',
      interestLevelLabel: typeof obj.interestLevelLabel === 'string' ? obj.interestLevelLabel : '',
      interestOptions: opts,
    }
  } catch {
    return null
  }
}

/** -- Clear last submission (e.g. when user clicks "Submit Another") */
export function clearLastSubmission(): void {
  try {
    localStorage.removeItem(LAST_SUBMISSION_KEY)
  } catch {
    // -- [ignore]
  }
}
